// "Swing damage" — a unit's estimated basic-attack burst in a single turn.
//
// The grade scores damage potential only via the WP-blind StatPA/StatMA buckets
// and the stat-blind global weapon win rate, so the stat×weapon interaction
// (a high-MA Oracle swinging an MA×WP Pole, doubled across a Dual-Wield pair)
// falls through the cracks. This distills the actual swing into one number so an
// empirical win-rate-by-swing dimension can capture it. Pure + data-injected,
// shared by tools/buildRosterStats.mjs (the backfill) and lib/unitGrade.js so
// the bucketing is identical on both sides.
//
// Validated full-history (tools/swingDamageProbe.mjs): the win rate climbs
// monotonically with swing and stays a +12–18pt separator at FIXED PA, so it's
// real signal the StatPA bucket misses — not a restatement of PA.

import { unitCombatStats } from './unitStats.js';
import { weaponDamage, unarmedDamage } from './damage.js';

// Reduce a weaponDamage() return — a number, a "min - max" random range, or a
// magical-gun "70% (a) - 20% (b) - 10% (c)" string — to a single EXPECTED value
// (the best estimate of average output, which is what we correlate with wins).
export function expectedSwingValue(d) {
  if (d == null) return null;
  if (typeof d === 'number') return d;
  const s = String(d);
  const pct = [...s.matchAll(/(\d+)%\s*\((\d+)\)/g)];
  if (pct.length) return pct.reduce((a, [, p, v]) => a + (+p / 100) * +v, 0);  // magical gun: probability-weighted
  const rng = s.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rng) return (+rng[1] + +rng[2]) / 2;                                       // axe/bag/flail random range
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// Total expected basic-attack damage for the unit's turn:
//   · Doublehand doubles the single mainhand (weaponDamage applies the ×2),
//   · a weapon in the offhand (Dual Wield, via the support OR innate e.g. Ninja)
//     adds a second swing — a shield/relic there has no attack formula so it
//     adds nothing,
//   · no weapon → the unarmed/Martial-Arts punch.
// Monsters return null (they attack with intrinsic skills, not weapon swings).
// `combat` (an existing unitCombatStats result) can be passed to skip the
// recompute — the backfill does this for all ~3.4M units.
export function unitSwingDamage(unit, charStats, itemschanged, notAbles, combat) {
  if (!unit || unit.Gender === 'Monster') return null;
  const c = combat || unitCombatStats(unit, charStats, itemschanged, notAbles);
  const { stats, paUnarmed, equipped, classBase } = c;
  const isMartialArts = unit.SupportSkill === 'Martial Arts' || unit.Class === 'Monk';
  const ctx = { pa: stats.pa, ma: stats.ma, speed: stats.speed, brave: unit.Brave || 0, faith: unit.Faith || 0 };
  const punch = () => expectedSwingValue(unarmedDamage({ paUnarmed, brave: ctx.brave, isMartialArts }));
  const swingOf = (item, doublehand) => {
    if (!item || !item.type) return null;
    return expectedSwingValue(weaponDamage({ item, ...ctx, doublehand, targetFaith: 100 }));
  };

  // Dual Wield — support OR innate (Ninja). The API spells out the rule on the
  // skill itself: "attacking once with each (or both fists if unarmed)". So an
  // UNARMED dual-wielder throws TWO punches (~2× a single fist); an armed one
  // adds the offhand weapon's swing (a lone weapon + empty hand stays single).
  const dualWield = unit.SupportSkill === 'Dual Wield' || ((classBase && classBase.innates) || []).some((i) => i && i.name === 'Dual Wield');

  const mainWeapon = swingOf(equipped.Mainhand, unit.SupportSkill === 'Doublehand');
  let main, off;
  if (mainWeapon == null) {            // unarmed → fists (punch() already carries Martial Arts ×1.5)
    const p = punch();
    main = p; off = dualWield ? p : 0; // both fists for a dual-wielder
  } else {
    main = mainWeapon;
    off = swingOf(equipped.Offhand, false) || 0;  // shield/relic/empty → no second swing
  }
  const total = (main || 0) + off;
  return total > 0 ? total : null;
}

// 50-wide buckets, capped at 350+. The string keys MUST stay identical between
// the backfill and the grade (both import this) or the lookups miss.
export function swingBucket(v) {
  if (v == null) return null;
  if (v >= 350) return '350+';
  const lo = Math.floor(v / 50) * 50;
  return lo < 50 ? '<50' : `${lo}-${lo + 49}`;
}
